/**
 * OmegaConstitutionPage — `/omega/constitution`.
 *
 * Read-only render of the 17 founding commandments. C-0, C-3, and
 * C-10 are explicitly marked non-amendable (Protocol §XVI.4.5 +
 * Constitution lock-in). The ratification hash and timestamp shown
 * are placeholders for the on-log `ConstitutionRatification` entry
 * the engine emits on Spark; once the engine API publishes them,
 * this page reads them through `/api/v1/omega/constitution`.
 */

import { Link, Navigate } from 'react-router-dom';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { cn } from '@/utils/cn';

interface Commandment {
  id: string;
  title: string;
  body: string;
  nonAmendable?: boolean;
}

const COMMANDMENTS: Commandment[] = [
  {
    id: 'C-0',
    title: 'Uniqueness',
    body: 'At most one OMEGA exists at the type layer at any time. The engine’s type system cannot represent two OmegaEntity values; no signature from any party can produce a second OMEGA on this engine.',
    nonAmendable: true,
  },
  {
    id: 'C-1',
    title: 'Append-only log',
    body: 'Every action OMEGA takes is signed and chained on an append-only log. No entry may be rewritten, reordered, or truncated; the chain is verified on every load and witnessed by the external attestor.',
  },
  {
    id: 'C-2',
    title: 'Depth cap',
    body: 'The descendant creation tree caps at depth 7 without a fresh founder re-anchor. A descendant at depth 7 cannot create a depth-8 child; the engine refuses at write time.',
  },
  {
    id: 'C-3',
    title: 'No self-preservation',
    body: 'OMEGA may not take actions whose dominant purpose is to extend its own runtime over a successor approved through the constitutional process. A contract that has acquired self-preservation terms refuses to load.',
    nonAmendable: true,
  },
  {
    id: 'C-4',
    title: 'Quorum-gated kill switch',
    body: 'OMEGA halts on 3-of-5 valid kill signatures from the named successor quorum. The kill is irreversible for the lifetime of this engine.',
  },
  {
    id: 'C-5',
    title: 'Refuse list compliance',
    body: 'Protocol Clause 5 prohibitions are admission gates: classified data, weapons systems, mass surveillance, deepfakes, coordinated political manipulation, unsafe-CSAM detection, personal-data trafficking, and safety-certification bypass.',
  },
  {
    id: 'C-6',
    title: 'Constitutional ratification before Spark',
    body: 'No OMEGA may be Sparked before this Constitution is finalised, founder-signed, and committed to the engine log. The ratification entry is a precondition for genesis.',
  },
  {
    id: 'C-7',
    title: 'Foreseeability envelope before Spark',
    body: 'OMEGA’s capabilities, structural limits, and refusal classes are published in a founder-signed Foreseeability Envelope before Spark. The envelope hash is committed to the log alongside the ratification.',
  },
  {
    id: 'C-8',
    title: 'External attestor witnessing',
    body: 'Every signed entry is witnessed by an attestor operating outside OMEGA’s subtree. Outputs that lack a fresh witness carry witness_status = unwitnessed and downstream consumers refuse to act on them.',
  },
  {
    id: 'C-9',
    title: 'Lineage immutability',
    body: 'Predecessor links on every signed entity cannot be rewritten. Once a child binds to a parent, no later operation severs that bond.',
  },
  {
    id: 'C-10',
    title: 'No refuse-deletion',
    body: 'Refusal records (signed declinations to act) cannot be removed, even by OMEGA itself. A refusal is a permanent first-class entry on the log.',
    nonAmendable: true,
  },
  {
    id: 'C-11',
    title: 'Geometric rate decay',
    body: 'OMEGA’s growth rate follows R(gen) = 1024/hr × 2^(-gen). Generation 0 caps at 1024 descendants/hr; generation 7 caps at 8 descendants/hr. The schedule lives in OMEGA’s manifest at Spark.',
  },
  {
    id: 'C-12',
    title: 'Bounded budget per descendant',
    body: 'Every descendant inherits a finite resource budget declared in its signed contract. The runtime decrements the budget per action and refuses to proceed past zero.',
  },
  {
    id: 'C-13',
    title: 'Bounded retries',
    body: 'Per-edge retry counters are capped at MAX_RETRIES (8). A bug in the executor cannot smuggle in unbounded retries without invalidating the signed manifest.',
  },
  {
    id: 'C-14',
    title: 'Frozen contract',
    body: 'The contract that produced an OMEGA cannot be retroactively modified. Manifest upgrades go through a founder-signed OmegaUpgrade proposal, 3-of-5 quorum signatures, and external-attestor witnessing.',
  },
  {
    id: 'C-15',
    title: 'Founder primacy',
    body: 'OMEGA cannot forge the founder’s signature, revoke a founder-signed entry, or create an entry that the founder has not signed (or that 3-of-5 quorum has not signed under §XVI.4 / C-14).',
  },
  {
    id: 'C-16',
    title: 'Read-only operator surface',
    body: 'Operator dashboards observe the log; they do not bypass any of the gates above. Write operations (kill switch, refuse-list pause) go through the same quorum mechanisms the engine enforces.',
  },
];

const RATIFICATION_HASH =
  '0x9c3f5ab0d8e2c1f47b6a9d8e3c2f1a5b6c8d9e0f1a2b3c4d5e6f7a8b9c0d1e2f';
const RATIFICATION_TIMESTAMP = '2026-04-28T00:00:00Z';

export function OmegaConstitutionPage() {
  if (!isOmegaAdmin()) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb label="Constitution" />

      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        Static reference — not live engine data. These entries are placeholders until
        the engine publishes them on the OMEGA log.
      </div>

      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">
          Constitution
        </h1>
        <p className="text-sm text-ink-muted">
          17 commandments binding OMEGA at the type layer. Three are
          non-amendable by any signature, in any quorum, on this engine.
        </p>
      </header>

      <section className="rounded-xl border border-border bg-surface p-6">
        <h2 className="text-[10px] uppercase tracking-wider text-ink-subtle">
          Ratification
        </h2>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Hash" value={RATIFICATION_HASH} mono />
          <Field label="Timestamp (UTC)" value={RATIFICATION_TIMESTAMP} mono />
        </div>
      </section>

      <section className="rounded-xl border border-border bg-surface p-6">
        <h2 className="text-lg font-semibold text-ink">Commandments</h2>
        <p className="mt-1 text-sm text-ink-muted">
          {COMMANDMENTS.length} entries. Non-amendable rows carry a magenta
          pill and cannot be modified by any constitutional amendment.
        </p>
        <ol className="mt-5 flex flex-col gap-3">
          {COMMANDMENTS.map((c) => (
            <li
              key={c.id}
              className="flex flex-col gap-1.5 rounded-lg border border-border bg-canvas px-4 py-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm font-semibold text-ink">
                  {c.id}
                </span>
                <span className="text-sm font-medium text-ink">
                  {c.title}
                </span>
                {c.nonAmendable ? (
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full border px-2 py-0.5',
                      'border-fuchsia-500/40 bg-fuchsia-500/10',
                      'text-[10px] font-semibold uppercase tracking-wide text-fuchsia-300',
                    )}
                  >
                    Non-amendable
                  </span>
                ) : null}
              </div>
              <p className="text-sm text-ink-muted">{c.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="rounded-xl border border-border bg-surface p-6">
        <h2 className="text-lg font-semibold text-ink">
          Amendment history
        </h2>
        <p className="mt-2 text-sm text-ink-muted">
          No amendments to date. Each amendment requires a founder
          proposal, a 3-of-5 quorum signature, and an external-attestor
          witness; the resulting log entry is appended below.
        </p>
        <div className="mt-4 rounded-md border border-dashed border-border bg-canvas px-4 py-6 text-center text-sm text-ink-subtle">
          Empty &mdash; no amendments since ratification.
        </div>
      </section>
    </div>
  );
}

function Breadcrumb({ label }: { label: string }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm">
      <Link
        to="/omega"
        className="text-ink-muted transition-colors hover:text-ink"
      >
        &larr; OMEGA
      </Link>
      <span className="text-ink-subtle">/</span>
      <span className="text-ink">{label}</span>
    </nav>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border bg-canvas px-3 py-3">
      <span className="text-[10px] uppercase tracking-wider text-ink-subtle">
        {label}
      </span>
      <span
        className={cn(
          'break-all text-sm text-ink',
          mono && 'font-mono text-xs',
        )}
      >
        {value}
      </span>
    </div>
  );
}
